import { useEffect, useRef } from 'react'

/**
 * The words, as they arrive.
 *
 * Committed text and the phrase still in flight are shown differently but both
 * are shown — a word that appears and then vanishes makes a parent stop and
 * check, which is exactly the hesitation this whole layer exists to remove.
 *
 * When transcription is unavailable the panel says so plainly and promises the
 * recording is still being kept, rather than sitting empty and looking broken.
 */

export function TranscriptDisplay({
  transcript,
  interim,
  listening,
  transcribing,
}: {
  transcript: string
  interim: string
  listening: boolean
  /** False when this browser has no live transcription. */
  transcribing: boolean
}) {
  const ref = useRef<HTMLParagraphElement>(null)

  // Follow the newest words rather than making the parent scroll mid-sentence.
  useEffect(() => {
    const el = ref.current
    if (el) el.scrollTop = el.scrollHeight
  }, [transcript, interim])

  const empty = !transcript && !interim

  return (
    <p ref={ref} className="capture-transcript" aria-live="polite" aria-atomic="false">
      {transcript}
      {interim && <span className="capture-interim">{transcript ? ' ' : ''}{interim}</span>}

      {empty && (
        <span className="capture-hint">
          {!transcribing
            ? 'Recording. Say what happened. You can fix the words after.'
            : listening
              ? 'Listening. Tell me how it went.'
              : 'Getting the microphone ready…'}
        </span>
      )}
    </p>
  )
}
